import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Fab, Paper, Tooltip } from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import styled from '@emotion/styled';
import AskAIConversation from './AskAIConversation';

// The running-timer bar is fixed to the bottom at 64px tall and shares this z-index, so anything
// anchored to the bottom-right has to clear it or it lands on the pause button.
const TIMER_BAR_HEIGHT = 64;

const LauncherFab = styled(Fab, { shouldForwardProp: (prop) => prop !== '$timerBar' })(({ theme, $timerBar }) => ({
    position: 'fixed',
    right: theme.spacing(3),
    bottom: $timerBar ? `calc(${theme.spacing(3)} + ${TIMER_BAR_HEIGHT}px)` : theme.spacing(3),
    zIndex: 1300,
    transition: theme.transitions.create('bottom', { duration: theme.transitions.duration.shorter }),
    [theme.breakpoints.down('sm')]: {
        right: theme.spacing(2),
        bottom: $timerBar ? `calc(${theme.spacing(2)} + ${TIMER_BAR_HEIGHT}px)` : theme.spacing(2),
    },
}));

const PanelContainer = styled(Paper, { shouldForwardProp: (prop) => prop !== '$timerBar' })(({ theme, $timerBar }) => ({
    position: 'fixed',
    right: theme.spacing(3),
    bottom: $timerBar ? `calc(${theme.spacing(3)} + ${TIMER_BAR_HEIGHT}px)` : theme.spacing(3),
    zIndex: 1300,
    width: 420,
    maxWidth: `calc(100vw - ${theme.spacing(4)})`,
    height: 560,
    // Clearing the bar costs height as well as offset, or the panel just overflows the top instead.
    maxHeight: $timerBar
        ? `calc(100vh - ${theme.spacing(12)} - ${TIMER_BAR_HEIGHT}px)`
        : `calc(100vh - ${theme.spacing(12)})`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        right: theme.spacing(1),
        bottom: $timerBar ? `calc(${theme.spacing(1)} + ${TIMER_BAR_HEIGHT}px)` : theme.spacing(1),
        maxHeight: $timerBar
            ? `calc(100vh - ${theme.spacing(10)} - ${TIMER_BAR_HEIGHT}px)`
            : `calc(100vh - ${theme.spacing(10)})`,
    },
}));

const AskAIChat = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const timerBarVisible = useSelector((state) => state.ui.timeTracking);
    const [open, setOpen] = useState(false);

    // Both surfaces share one transcript in session storage, so showing them together would run two
    // pollers over the same pending answer.
    if (location.pathname === '/ask') return null;

    if (!open) {
        return (
            <Tooltip title="Ask about system data" placement="left">
                <LauncherFab color="primary" aria-label="Ask about system data" onClick={() => setOpen(true)} $timerBar={timerBarVisible}>
                    <Badge color="error" variant="dot" invisible>
                        <QuestionAnswerIcon />
                    </Badge>
                </LauncherFab>
            </Tooltip>
        );
    }

    return (
        <PanelContainer elevation={8} $timerBar={timerBarVisible}>
            <AskAIConversation variant="panel" onClose={() => setOpen(false)} onExpand={() => navigate('/ask')} />
        </PanelContainer>
    );
};

export default AskAIChat;
